/**
 * Contexto de autenticación
 * Maneja el estado de autenticación del usuario, token y menú
 */

import { createContext, useContext, useState, ReactNode } from 'react';
import type { User, MenuItem } from '../types';

// Keys para localStorage
const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';
const AUTH_MENU_KEY = 'auth_menu';

// Keys para sessionStorage (datos sensibles de admisiones)
const ADMISSIONS_SUBMISSION_ID_KEY = 'admissions_submission_id';
const ADMISSIONS_RESPONDENT_ID_KEY = 'admissions_respondent_id';
const ADMISSIONS_EMAIL_KEY = 'admissions_email';

interface AuthContextType {
  user: User | null;
  menu: MenuItem[] | null;
  token: string | null;
  setAuth: (user: User, menu: MenuItem[], token: string) => void;
  logout: () => void;
  logoutWithExpirationMessage: () => void;
  isAuthenticated: boolean;
  showSessionExpiredModal: boolean;
  closeSessionExpiredModal: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Limpia todos los datos sensibles de localStorage y sessionStorage
 */
function clearAllSensitiveData() {
  // Limpiar localStorage
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(AUTH_MENU_KEY);

  // Limpiar sessionStorage
  sessionStorage.removeItem(ADMISSIONS_SUBMISSION_ID_KEY);
  sessionStorage.removeItem(ADMISSIONS_RESPONDENT_ID_KEY);
  sessionStorage.removeItem(ADMISSIONS_EMAIL_KEY);
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Cargar datos desde localStorage si existen
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  });
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem(AUTH_USER_KEY);
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [menu, setMenu] = useState<MenuItem[] | null>(() => {
    const storedMenu = localStorage.getItem(AUTH_MENU_KEY);
    return storedMenu ? JSON.parse(storedMenu) : null;
  });
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);

  const setAuth = (newUser: User, newMenu: MenuItem[], newToken: string) => {
    setUser(newUser);
    setMenu(newMenu);
    setToken(newToken);
    // Persistir todo en localStorage
    localStorage.setItem(AUTH_TOKEN_KEY, newToken);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(newUser));
    localStorage.setItem(AUTH_MENU_KEY, JSON.stringify(newMenu));
  };

  const logout = () => {
    setUser(null);
    setMenu(null);
    setToken(null);
    clearAllSensitiveData();
  };

  const logoutWithExpirationMessage = () => {
    setUser(null);
    setMenu(null);
    setToken(null);
    clearAllSensitiveData();
    setShowSessionExpiredModal(true);
  };

  const closeSessionExpiredModal = () => {
    setShowSessionExpiredModal(false);
  };

  const value: AuthContextType = {
    user,
    menu,
    token,
    setAuth,
    logout,
    logoutWithExpirationMessage,
    isAuthenticated: !!token && !!user && !!menu,
    showSessionExpiredModal,
    closeSessionExpiredModal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook para acceder al contexto de autenticación
 * @throws Error si se usa fuera del AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

